import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Infinity as InfinityIcon, CalendarClock, CalendarDays, Heart } from "lucide-react";

const DONATIONS = [
  { key: "donate_5", label: "$5" },
  { key: "donate_15", label: "$15" },
  { key: "donate_30", label: "$30" },
  { key: "donate_50", label: "$50" },
];

function PlanCard({ featured, badge, title, price, sub, bullets, cta, onClick, busy, testid }) {
  return (
    <div
      data-testid={testid}
      className={`relative bg-surface border p-8 flex flex-col ${
        featured ? "border-oxblood offset-shadow" : "border-archive-border"
      }`}
    >
      {badge && (
        <div className="absolute -top-3 left-8 bg-oxblood text-parchment font-mono text-[10px] tracking-widest uppercase px-3 py-1">
          {badge}
        </div>
      )}
      <h3 className="font-serif text-3xl text-ink tracking-tight">{title}</h3>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-serif text-5xl text-ink">{price}</span>
        {sub && <span className="font-mono text-xs tracking-widest uppercase text-text-muted">{sub}</span>}
      </div>
      <ul className="mt-6 space-y-3 flex-1">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-3 font-body text-ink">
            <Check strokeWidth={1.5} className="w-4 h-4 text-oxblood mt-1 shrink-0" />
            {b}
          </li>
        ))}
      </ul>
      <button
        onClick={onClick}
        disabled={busy}
        data-testid={`${testid}-button`}
        className="mt-8 w-full bg-oxblood text-parchment py-4 font-mono text-xs tracking-widest uppercase hover:bg-oxblood-dark transition-colors disabled:opacity-60"
      >
        {busy ? "Redirecting…" : cta}
      </button>
    </div>
  );
}

export default function Membership() {
  const { user } = useAuth();
  const [busy, setBusy] = useState("");

  const checkout = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy("");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <div className="max-w-2xl mb-14">
        <div className="font-mono text-xs tracking-[0.3em] uppercase text-oxblood mb-4">Membership</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink tracking-tight leading-none">
          Join the discussion
        </h1>
        <p className="font-body text-text-muted mt-6 leading-relaxed">
          Reading the weekly pages is always free. To add your voice to the discussion threads,
          become a member. Choose the arrangement that suits you.
        </p>
        {user?.has_membership && (
          <div
            data-testid="active-membership-banner"
            className="mt-6 inline-flex items-center gap-2 border border-oxblood bg-oxblood/5 text-oxblood px-4 py-2 font-mono text-xs tracking-widest uppercase"
          >
            <Check strokeWidth={1.5} className="w-4 h-4" />
            You are a {user.membership_type} member
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <PlanCard
          testid="plan-monthly"
          title={<span className="flex items-center gap-2"><CalendarClock strokeWidth={1.5} className="w-6 h-6 text-oxblood" /> Monthly</span>}
          price="$5"
          sub="/ month"
          bullets={["Post in every discussion thread", "Reply to fellow members", "Cancel anytime"]}
          cta="Subscribe Monthly"
          busy={busy === "membership_monthly"}
          onClick={() => checkout("membership_monthly")}
        />
        <PlanCard
          featured
          badge="Save 20%"
          testid="plan-annual"
          title={<span className="flex items-center gap-2"><CalendarDays strokeWidth={1.5} className="w-6 h-6 text-oxblood" /> Annual</span>}
          price="$48"
          sub="/ year"
          bullets={["Everything in Monthly", "Two months free vs monthly", "Billed once a year"]}
          cta="Subscribe Annually"
          busy={busy === "membership_annual"}
          onClick={() => checkout("membership_annual")}
        />
        <PlanCard
          testid="plan-lifetime"
          title={<span className="flex items-center gap-2"><InfinityIcon strokeWidth={1.5} className="w-6 h-6 text-oxblood" /> Lifetime</span>}
          price="$99"
          sub="one-time"
          bullets={["Everything in Annual", "Pay once, discuss forever", "Support the archive's preservation"]}
          cta="Get Lifetime Access"
          busy={busy === "membership_lifetime"}
          onClick={() => checkout("membership_lifetime")}
        />
      </div>

      {/* Donations */}
      <div className="mt-16 border-t border-archive-border pt-12">
        <div className="flex items-center gap-3 mb-3">
          <Heart strokeWidth={1.5} className="w-6 h-6 text-oxblood" />
          <h2 className="font-serif text-3xl text-ink tracking-tight">Support the Archive</h2>
        </div>
        <p className="font-body text-text-muted max-w-2xl mb-8 leading-relaxed">
          A one-time gift, of any size, helps preserve and digitise more of the fragile 1872 volume.
          Donations are gratefully received and entirely optional.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
          {DONATIONS.map((d) => (
            <button
              key={d.key}
              onClick={() => checkout(d.key)}
              disabled={busy === d.key}
              data-testid={`donate-${d.key}`}
              className="bg-surface border border-archive-border py-6 font-serif text-2xl text-ink hover:border-oxblood hover:text-oxblood transition-colors disabled:opacity-60"
            >
              {busy === d.key ? "…" : d.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
